import { Suspense, type ReactNode } from 'react';

import { createBrowserRouter, type RouteObject } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { FeedSkeleton } from '@/components/skeletons/PostCardSkeleton';
import { FeedPage } from '@/pages/FeedPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

import { FeedSidebar } from '@/features/feed/FeedSidebar';
import { env } from '@/lib/env';

import {
  LazyBookmarksPage,
  LazyForgotPasswordPage,
  LazyHallOfFamePage,
  LazyBusinessDashboardPage,
  LazyBusinessInboxPage,
  LazyBusinessProfileRedirect,
  LazyTransparencyPage,
  LazyLoginPage,
  LazyMapPage,
  LazyModerationPage,
  LazyNotificationsPage,
  LazyPostDetailPage,
  LazyProfilePage,
  LazyRegisterPage,
  LazyResetPasswordPage,
  LazySearchPage,
  LazySettingsPage,
  LazyStyleguidePage,
  LazyVerifyEmailPage,
  LazyAcceptInvitePage,
  LazyCompleteRegistrationPage,
  LazyForBusinessPage,
} from './lazy-routes';
import { RedirectIfSignedIn } from './RedirectIfSignedIn';
import { RequireAuth } from './RequireAuth';
import { RequireRole } from './RequireRole';
import { RootErrorBoundary } from './RootErrorBoundary';

/**
 * Wraps a code-split route in the same skeleton the page itself shows while
 * loading, so a slow chunk and a slow request look identical rather than
 * flashing an empty screen between them.
 */
function lazyRoute(element: ReactNode): ReactNode {
  return <Suspense fallback={<FeedSkeleton count={2} />}>{element}</Suspense>;
}

const routes: RouteObject[] = [
  {
    errorElement: <RootErrorBoundary />,
    children: [
      // Auth screens sit outside the app shell: navigation and a compose button
      // are noise when the only task is signing in.
      {
        element: <RedirectIfSignedIn />,
        children: [
          { path: '/login', element: lazyRoute(<LazyLoginPage />) },
          { path: '/register', element: lazyRoute(<LazyRegisterPage />) },
          { path: '/forgot-password', element: lazyRoute(<LazyForgotPasswordPage />) },
        ],
      },
      { path: '/reset-password', element: lazyRoute(<LazyResetPasswordPage />) },
      { path: '/verify-email', element: lazyRoute(<LazyVerifyEmailPage />) },
      // Where Stripe returns somebody who has just paid for an account.
      { path: '/register/complete', element: lazyRoute(<LazyCompleteRegistrationPage />) },

      { path: '/dev/styleguide', element: lazyRoute(<LazyStyleguidePage />) },

      // The sidebar carries the feed's filters, its Town Pulse and its trending
      // tags — all of which describe the feed and nothing else. Two shells
      // rather than one, so a moderator reading a report queue is not also
      // being offered a register filter.
      {
        path: '/',
        element: <AppShell sidebar={<FeedSidebar />} />,
        children: [{ index: true, element: <FeedPage /> }],
      },

      // The map gets the full width of the page: it is a picture, and the
      // sidebar's 300px is another slice of the world.
      {
        path: '/',
        element: <AppShell wide />,
        children: [{ path: 'map', element: lazyRoute(<LazyMapPage />) }],
      },

      {
        path: '/',
        element: <AppShell />,
        children: [
          { path: 'posts/:id', element: lazyRoute(<LazyPostDetailPage />) },
          // Public: anyone can read a profile, signed in or not.
          { path: 'u/:username', element: lazyRoute(<LazyProfilePage />) },
          // Public and crawlable: "{name} {town}" is what people search for.
          ...(env.VITE_BUSINESS_ACCOUNTS_ENABLED
            ? [
                // Kept as a redirect rather than removed: every link already shared,
                // every bookmark and every search result still lands somewhere.
                { path: 'b/:slug', element: lazyRoute(<LazyBusinessProfileRedirect />) },
                // The page a business lands on before it has an account.
                { path: 'for-business', element: lazyRoute(<LazyForBusinessPage />) },
                // Behind the flag with the rest, and behind a paid plan at the
                // API — the page renders the refusal rather than hiding.
                // Its own path, not under /b/ — that prefix redirects to the
                // profile now, and a page whose address begins with one that
                // means "somewhere else" is a page waiting to be misrouted.
                { path: 'dashboard', element: lazyRoute(<LazyBusinessDashboardPage />) },
                // Its own page rather than a settings tab: this is the list a
                // business opens to find work, not the panel where the machine
                // that produces it is configured.
                { path: 'inbox', element: lazyRoute(<LazyBusinessInboxPage />) },
              ]
            : []),
          { path: 'search', element: lazyRoute(<LazySearchPage />) },

          { path: 'ideals', element: lazyRoute(<LazyHallOfFamePage />) },
          // Public and unauthenticated on purpose: the numbers are only worth
          // anything if anybody can read them without asking (spec §11).
          { path: 'transparency', element: lazyRoute(<LazyTransparencyPage />) },
          {
            element: <RequireAuth />,
            children: [
              // An invitation is answered by being the invited account, so
              // signing in comes first — and RequireAuth brings them back here.
              { path: 'invites/:token', element: lazyRoute(<LazyAcceptInvitePage />) },
              { path: 'notifications', element: lazyRoute(<LazyNotificationsPage />) },
              { path: 'bookmarks', element: lazyRoute(<LazyBookmarksPage />) },
              { path: 'profile', element: lazyRoute(<LazyProfilePage />) },
              { path: 'settings', element: lazyRoute(<LazySettingsPage />) },
            ],
          },
          {
            element: <RequireRole roles={['MODERATOR', 'ADMIN']} />,
            children: [{ path: 'admin', element: lazyRoute(<LazyModerationPage />) }],
          },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes, {
  future: {
    v7_relativeSplatPath: true,
    v7_fetcherPersist: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true,
  },
});
